/**
 * Multi-currency money helpers.
 *
 * Canonical internal representation is `bigint` minor units (see CLAUDE.md §2).
 * Different currencies have different minor-unit exponents:
 *   - 2dp: THB, USD, EUR, GBP, AUD, CNY, SGD, MYR, IDR, PHP, INR, … (multiplier 100)
 *   - 0dp: JPY, KRW, VND, LAK, … (multiplier 1)
 *   - 3dp: TND, BHD, KWD, OMR, JOD, … (multiplier 1000)
 *
 * Helpers convert at the conversion-boundaries described in CLAUDE.md §2.3
 * (HTTP request validation, DB, outbound provider payloads). They never round
 * silently — `toMinor` truncates toward zero past the supported precision.
 */

const ZERO_DP = new Set<string>(['JPY', 'KRW', 'VND', 'LAK', 'IDR_OLD', 'CLP', 'PYG', 'RWF', 'XAF', 'XOF']);
const THREE_DP = new Set<string>(['TND', 'BHD', 'KWD', 'OMR', 'JOD', 'IQD', 'LYD']);

/**
 * Currencies the platform will accept as an organization's denomination.
 *
 * `multiplier` deliberately falls back to 100 (2dp) for anything it does not
 * recognise, which is the right default for a formatting helper but the wrong
 * one for a value that is written once and then immutable: a mistyped code on a
 * 0dp organization is silently priced 100x wrong for the life of the tenant, and
 * `wallet.wallets.currency` can never be corrected in place (§2.7). So the codes
 * that may be *chosen* are enumerated, while the codes that may be *formatted*
 * stay permissive.
 */
const TWO_DP_ACCEPTED = new Set<string>([
  'THB', 'USD', 'EUR', 'GBP', 'AUD', 'CAD', 'CHF', 'NZD', 'HKD', 'TWD',
  'CNY', 'SGD', 'MYR', 'IDR', 'PHP', 'INR', 'BND', 'MMK', 'KHR', 'BDT',
  'LKR', 'NPR', 'PKR', 'AED', 'SAR', 'QAR', 'TRY', 'ZAR', 'BRL', 'MXN',
]);

/**
 * True when the code is one the platform is willing to denominate an
 * organization, wallet or ledger in. Use at the boundary where a currency is
 * CHOSEN (organization create); never as a gate on formatting an existing value.
 */
export function isSupportedCurrency(currency: string): boolean {
  const code = currency.toUpperCase();
  // ZERO_DP carries the non-ISO `IDR_OLD` marker; an organization code is always
  // a 3-letter ISO 4217 code, so the length check keeps that marker unselectable.
  if (!/^[A-Z]{3}$/.test(code)) return false;
  return TWO_DP_ACCEPTED.has(code) || ZERO_DP.has(code) || THREE_DP.has(code);
}

/** Every currency an organization may be denominated in, sorted for display. */
export function supportedCurrencies(): string[] {
  return [...TWO_DP_ACCEPTED, ...ZERO_DP, ...THREE_DP]
    .filter((c) => /^[A-Z]{3}$/.test(c))
    .sort();
}

/** Return the integer multiplier (10^exponent) for a currency. Defaults to 100 (2dp). */
export function multiplier(currency: string): bigint {
  const code = currency.toUpperCase();
  if (ZERO_DP.has(code)) return 1n;
  if (THREE_DP.has(code)) return 1000n;
  return 100n;
}

/** Return the number of fractional digits used by the currency. */
export function decimalPlaces(currency: string): number {
  const code = currency.toUpperCase();
  if (ZERO_DP.has(code)) return 0;
  if (THREE_DP.has(code)) return 3;
  return 2;
}

/**
 * Parse a decimal string into bigint minor units for the given currency.
 * Truncates toward zero — never rounds. Caller's responsibility to reject
 * over-precision input if business logic requires it.
 *
 *   toMinor('100.50', 'THB') → 10050n
 *   toMinor('100',    'JPY') → 100n
 *   toMinor('100.123', 'TND') → 100123n
 *   toMinor('100.999', 'THB') → 10099n  (truncated, NOT rounded)
 */
export function toMinor(amount: string, currency: string): bigint {
  const dp = decimalPlaces(currency);
  const trimmed = amount.trim();
  if (trimmed === '') throw new Error('toMinor: empty amount');
  const negative = trimmed.startsWith('-');
  const body = negative ? trimmed.slice(1) : trimmed;
  const parts = body.split('.');
  if (parts.length > 2) {
    throw new Error(`toMinor: malformed amount "${amount}" (multiple decimal points)`);
  }
  const [whole = '0', fracRaw = ''] = parts;
  // A trailing dot ("100.") yields an empty fragment that would otherwise be
  // silently zero-padded — reject it rather than accept a garbled amount.
  if (parts.length === 2 && fracRaw === '') {
    throw new Error(`toMinor: malformed amount "${amount}" (empty fractional part)`);
  }
  if (!/^\d+$/.test(whole)) throw new Error(`toMinor: invalid whole part "${whole}"`);
  if (fracRaw !== '' && !/^\d+$/.test(fracRaw)) {
    throw new Error(`toMinor: invalid fractional part "${fracRaw}"`);
  }
  const frac = dp === 0 ? '' : (fracRaw + '0'.repeat(dp)).slice(0, dp);
  const minor = dp === 0 ? BigInt(whole) : BigInt(whole) * (10n ** BigInt(dp)) + BigInt(frac);
  return negative ? -minor : minor;
}

/**
 * Format bigint minor units back to the canonical decimal string for the
 * given currency. Always emits the full fractional precision (or none for
 * zero-dp currencies).
 *
 *   fromMinor(10050n, 'THB') → '100.50'
 *   fromMinor(100n,   'JPY') → '100'
 *   fromMinor(100123n, 'TND') → '100.123'
 */
export function fromMinor(amount: bigint, currency: string): string {
  const dp = decimalPlaces(currency);
  const sign = amount < 0n ? '-' : '';
  const abs = amount < 0n ? -amount : amount;
  if (dp === 0) return `${sign}${abs.toString()}`;
  const mul = 10n ** BigInt(dp);
  const whole = abs / mul;
  const frac = abs % mul;
  return `${sign}${whole.toString()}.${frac.toString().padStart(dp, '0')}`;
}

/**
 * The wire shape for a money figure travelling inside an error's `extensions`.
 *
 * A bare minor-unit integer is not renderable: the reader has to assume an
 * exponent, and assuming 2dp prints a JPY or KRW figure at 100x (§2.7). Pairing
 * the amount with its currency makes the value self-describing, so the client
 * formats it with the same exponent it was denominated in.
 *
 * The amount is a string because `bigint` is not JSON-serializable and `number`
 * silently rounds past 2^53.
 */
export interface MoneyExtension {
  amount: string;
  currency: string;
}

export function moneyExtension(amount: bigint, currency: string): MoneyExtension {
  return { amount: amount.toString(), currency: currency.toUpperCase() };
}
